import { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual, throttle } from 'lodash-es';
import { useDispatch } from 'react-redux';

import { openModal } from 'actions/modal';
import { stopRecurringRetries } from 'api/recurring';
import DateHelpers from 'helpers/Date';
import Operations from 'helpers/Operations';
import Messages from 'constants/rpcTypes';
import permissionReasons from 'constants/permissionReasons';
import breakpoints from 'constants/breakpoints';
import PaymentPageUI from 'types/PaymentPageUI';
import PaymentItemType from 'types/savedEntity/SavedPayment';
import { Config } from 'components/paymentCard/PaymentCardContainer';
import { WithPermissions } from 'decorators/addPermissions';
import { IntlProps } from 'decorators/addTranslation';
import Icon from 'components/ui/icon';
import React from 'react';

type OrderType = 'asc' | 'desc';

interface UsePaymentCardProps
    extends Pick<IntlProps, 'getTranslate'>,
    Pick<WithPermissions, 'isEnabled' | 'isDisabledByReason'> {
    payment?: PaymentItemType;
    config?: Config;
    isLoading: boolean;
    syncPaymentUI: (id: string, ui: PaymentPageUI) => void;
    fetchData: () => void;
}

const setPaymentInfoState = () =>
    document.body.clientWidth >= breakpoints.commonTabletL;

export function usePaymentCard(props: UsePaymentCardProps) {
    const {
        payment,
        isLoading,
        getTranslate,
        fetchData,
        syncPaymentUI,
        isEnabled,
        isDisabledByReason,
        config,
    } = props;

    const dispatch = useDispatch();
    const [pageUI, setPageUI] = useState<PaymentPageUI>(() => ({
        pageScrollTop: 0,
        operationsScrollTop: 0,
        selectedOperationId: '',
        operationInfoScrollTop: 0,
        paymentInfoScrollTop: 0,
        paymentSelectedTab: 0,
        parentSelectedOperationId: '',
        isRetriesOpen: false,
        operationOrder: 'desc',
        ...payment?.ui,
    }));

    const [isOpenedPaymentInfo, setIsOpenedPaymentInfo] = useState(
        setPaymentInfoState
    );
    const [isActionsOpened, setIsActionsOpened] = useState(false);

    const prevPaymentRef = useRef(payment);
    const prevPageUIRef = useRef(pageUI);
    const changePageUI = useCallback(
        (newPageUI: Partial<PaymentPageUI>, callback?: () => void) => {
            setPageUI((prev) => {
                const next = { ...prev, ...newPageUI };
                return next;
            });
            if (callback) {
                setTimeout(callback, 0);
            }
        },
        []
    );

    const openPaymentInfo = useCallback(
        () => setIsOpenedPaymentInfo(true),
        []
    );
    const closePaymentInfo = useCallback(
        () => setIsOpenedPaymentInfo(false),
        []
    );

    useEffect(() => {
        const handleResize = throttle(() => {
            if (document.body.clientWidth < breakpoints.commonTabletL) {
                closePaymentInfo();
            } else {
                openPaymentInfo();
            }
        }, 500);

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [closePaymentInfo, openPaymentInfo]);

    useEffect(() => {
        const prevPayment = prevPaymentRef.current;

        if (!payment || !prevPayment) {
            prevPaymentRef.current = payment;
            return;
        }

        if (prevPayment.id !== payment.id) {
            syncPaymentUI(prevPayment.id, prevPageUIRef.current);
        }

        if (!isEqual(prevPayment, payment)) {
            if (payment?.ui) {
                let nextPageUI = { ...payment.ui } as PaymentPageUI;

                if (!nextPageUI.selectedOperationId && !nextPageUI.isRetriesOpen) {
                    nextPageUI = {
                        ...nextPageUI,
                        selectedOperationId: payment?.data
                            ? getInitialSelectedOperationFromData(
                                payment,
                                nextPageUI,
                                pageUI
                            )
                            : '',
                    };
                }

                setPageUI(nextPageUI);
            }
        }

        prevPaymentRef.current = payment;
    }, [payment, syncPaymentUI]);

    useEffect(() => {
        prevPageUIRef.current = pageUI;
    }, [pageUI]);

    const changeSort = useCallback(() => {
        setPageUI((prev) => ({
            ...prev,
            operationOrder: prev.operationOrder === 'desc' ? 'asc' : 'desc',
        }));
    }, []);

    const openRetries = useCallback(
        (operationId: string) => {
            setPageUI((prev) => ({
                ...prev,
                parentSelectedOperationId: operationId,
                isRetriesOpen: true,
            }));
            setTimeout(() => {
                setPageUI((prev) => {
                    const ops = payment?.data?.operations;
                    if (!ops) return prev;
                    const retryOps = getRetryOperations(ops, operationId);
                    const initial = getInitialSelected(retryOps, prev.operationOrder as OrderType);
                    return { ...prev, selectedOperationId: initial };
                });
            }, 0);
        },
        [payment]
    );

    const closeRetries = useCallback(() => {
        setPageUI((prev) => ({
            ...prev,
            parentSelectedOperationId: '',
            isRetriesOpen: false,
            selectedOperationId: prev.parentSelectedOperationId,
        }));
    }, []);

    const openRetrySuccessModal = useCallback(() => {
        dispatch(
            openModal({
                modalId: 'Notification',
                content: {
                    icon: React.createElement(
                        'div',
                        {
                            className:
                                'bg-icon-status-success-secondary flex h-[60px] w-[60px] items-center justify-center rounded-full',
                        },
                        React.createElement(Icon, {
                            name: 'im-Check-mark',
                            size: 38,
                            className: 'text-icon-status-success-primary',
                        })
                    ),
                    title: getTranslate('payment.single.modal.retriesStopped.header'),
                    text: getTranslate('payment.single.modal.retriesStopped.text'),
                    okButton: {
                        text: getTranslate('payment.single.modal.response.button'),
                        status: 'outline',
                    },
                },
            })
        );
    }, [dispatch, getTranslate]);

    const openRetryWarningModal = useCallback(() => {
        dispatch(
            openModal({
                modalId: 'Notification',
                content: {
                    icon: React.createElement(
                        'div',
                        {
                            className:
                                'bg-icon-status-warning-secondary flex h-[60px] w-[60px] items-center justify-center rounded-full',
                        },
                        React.createElement(Icon, {
                            name: 'im-Error',
                            size: 38,
                            className: 'text-icon-status-warning-primary',
                        })
                    ),
                    title: getTranslate(
                        'payment.single.modal.retriesStoppedWrong.header'
                    ),
                    text: getTranslate('payment.single.modal.retriesStoppedWrong.text'),
                    okButton: {
                        text: getTranslate('payment.single.modal.response.button'),
                        status: 'outline',
                    },
                },
            })
        );
    }, [dispatch, getTranslate]);

    const openRetryCancelModal = useCallback(() => {
        dispatch(
            openModal({
                modalId: 'Confirm',
                content: {
                    icon: React.createElement(
                        'div',
                        {
                            className:
                                'bg-icon-status-error-secondary flex h-[60px] w-[60px] items-center justify-center rounded-full',
                        },
                        React.createElement(Icon, {
                            name: 'im-Info-Hint',
                            size: 38,
                            className: 'text-icon-status-error-primary',
                        })
                    ),
                    title: getTranslate(
                        'payment.single.modal.cancelRetries.header'
                    ),
                    text: getTranslate('payment.single.modal.cancelRetries.text'),
                    okButton: {
                        text: getTranslate(
                            'payment.single.modal.cancelRetries.okButton'
                        ),
                        status: 'danger',
                    },
                    cancelButton: {
                        text: getTranslate(
                            'payment.single.modal.cancelRetries.cancelButton'
                        ),
                        status: 'danger-outline',
                    },
                },
                callback: (isAllowed: boolean) => {
                    if (isAllowed) {
                        stopRetries();
                    }
                },
            })
        );
    }, [dispatch, getTranslate]);

    const stopRetries = useCallback(async () => {
        if (!pageUI.selectedOperationId || !payment?.id) return;

        try {
            const { status } = await stopRecurringRetries(
                payment.id,
                pageUI.selectedOperationId
            );
            if (status === 'success') {
                fetchData();
                openRetrySuccessModal();
            }
        } catch {
            openRetryWarningModal();
        }
    }, [
        payment,
        pageUI.selectedOperationId,
        fetchData,
        openRetrySuccessModal,
        openRetryWarningModal,
    ]);

    const getSortedOperations = useCallback(
        (operations: any[], order: OrderType) =>
            [...operations].sort((o1, o2) => {
                const date1 = +DateHelpers.createDate(
                    o1.creationDate,
                    'datetime'
                ).toDate();
                const date2 = +DateHelpers.createDate(
                    o2.creationDate,
                    'datetime'
                ).toDate();
                return order === 'asc' ? date1 - date2 : date2 - date1;
            }),
        []
    );

    const getCurrentOperations = useCallback(
        (operations: any[]) => {
            const { parentSelectedOperationId, isRetriesOpen } = pageUI;
            if (isRetriesOpen) {
                const parent = operations.find(
                    ({ operationId }) => operationId === parentSelectedOperationId
                );
                return parent ? [...parent.recurring_retry_operations] : [...operations];
            }
            return [...operations];
        },
        [pageUI]
    );

    const getSelectedOperation = useCallback(
        (operations: any[]) => {
            const { selectedOperationId } = pageUI;
            return (
                operations.find(
                    ({ operationId }) => selectedOperationId === operationId
                ) || operations[0]
            );
        },
        [pageUI]
    );

    const getCurrentSelectedOperation = useCallback(
        (currentOperations: any[]) => {
            const sorted = getSortedOperations(
                currentOperations,
                pageUI.operationOrder as OrderType
            );
            return getSelectedOperation(sorted);
        },
        [getSortedOperations, getSelectedOperation, pageUI.operationOrder]
    );

    const getStatusTooltip = useCallback(
        (status: string, retryDate: string) => {
            switch (status) {
                case 'retry in progress':
                    return getTranslate('payment.single.retryprogress.label');
                case 'retries active':
                    return retryDate
                        ? React.createElement(
                            'div',
                            null,
                            getTranslate('payment.single.retryactivedate.label'),
                            React.createElement(
                                'span',
                                { className: 'text-m-semi-bold' },
                                ` ${retryDate}`
                            )
                        )
                        : getTranslate('payment.single.retryactivenodate.label');
                case 'retries unsuccessful':
                    return getTranslate('payment.single.retryunsuccesful.label');
                case 'retries canceled':
                    return getTranslate('payment.single.retrycancelled.label');
                default:
                    return '';
            }
        },
        [getTranslate]
    );

    const { operations } = payment?.data || {};
    const operationsDuration =
        !isLoading && operations
            ? Operations.getDuration(operations || [])
            : '';

    return {
        // state
        pageUI,
        isOpenedPaymentInfo,
        isActionsOpened,
        // setters
        changePageUI,
        setIsActionsOpened,
        // payment info panel
        openPaymentInfo,
        closePaymentInfo,
        // sort
        changeSort,
        // retries
        openRetries,
        closeRetries,
        openRetryCancelModal,
        stopRetries,
        // operation helpers
        getSortedOperations,
        getCurrentOperations,
        getSelectedOperation,
        getCurrentSelectedOperation,
        getStatusTooltip,
        // derived
        operationsDuration,
        // permissions
        isEnabled,
        isDisabledByReason,
        config,
    };
}

function getRetryOperations(operations: any[], parentOperationId: string) {
    const parent = operations.find(
        ({ operationId }) => operationId === parentOperationId
    );
    return parent ? [...parent.recurring_retry_operations] : [];
}

function getInitialSelected(operations: any[], order: OrderType): string {
    if (!operations.length) return '';
    const sorted = [...operations].sort((o1, o2) => {
        const d1 = +DateHelpers.createDate(o1.creationDate, 'datetime').toDate();
        const d2 = +DateHelpers.createDate(o2.creationDate, 'datetime').toDate();
        return order === 'asc' ? d1 - d2 : d2 - d1;
    });
    return sorted[0]?.operationId ?? '';
}

function getInitialSelectedOperationFromData(
    payment: PaymentItemType,
    nextPageUI: PaymentPageUI,
    currentPageUI: PaymentPageUI
): string {
    const { operations } = payment?.data || {};
    if (!operations) return '';

    const order = (nextPageUI.operationOrder ??
        currentPageUI.operationOrder) as OrderType;
    const { parentSelectedOperationId, isRetriesOpen } = nextPageUI;

    let currentOps = [...operations];
    if (isRetriesOpen && parentSelectedOperationId) {
        currentOps = getRetryOperations(operations, parentSelectedOperationId);
    }

    return getInitialSelected(currentOps, order);
}
